import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AbinResultDto } from '../../../../inge-api/abinclient/models';
import { ButtonGroupItemData } from '../../../../shared-report-components/base-components/fordjupning-button-group/button-group-item.model';
import { ChartCategory, ColorMapService } from '../../../../common/service/color-mapper.service';
import { BehaviorSubject } from 'rxjs';
import { UiTableConfig } from '../../../../ui-components/ui-table/ui-table/models/tableConfig';
import { formatNumber } from '@angular/common';
import { round } from 'lodash-es';

@Component({
    selector: 'app-kombo-tradslagsammansattning',
    templateUrl: './kombo-tradslagsammansattning.component.html',
    styleUrl: './kombo-tradslagsammansattning.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KomboTradslagensSammansattningComponent implements OnInit {
    tableConfig: UiTableConfig = {
        columns: [
            { title: 'Trädslag', dataProperty: 'tradslag' },
            { title: 'Alla bestånd', dataProperty: 'allaBestand' },
            { title: 'Föryngrad med tall', dataProperty: 'foryngradMedTall' },
            { title: 'Föryngrad med gran', dataProperty: 'foryngradMedGran' }
        ],
        rowActions: []
    };

    tableCaption = '';
    tableContent = [];

    private chartEntries = [
        {
            name: 'Tall',
            key: 'antalTallarHa3InvAVG',
            color: ChartCategory.TALL
        },
        {
            name: 'Gran',
            key: 'antalGranarHa3InvAVG',
            color: ChartCategory.GRAN
        },
        {
            name: 'Björk',
            key: 'antalBjorkarHa3InvAVG',
            color: ChartCategory.BJORK
        },
        {
            name: 'Övrigt',
            key: 'antalOvrigtHa3InvAVG',
            color: ChartCategory.OVRIG
        },
        {
            name: 'RASE',
            key: 'antalRASEHa3InvAVG',
            color: ChartCategory.RASE
        }
    ];

    options: any = {
        chartArea: { width: '90%', height: '90%' },
        height: 230,
        pieSliceText: 'none',
        tooltip: {
            trigger: 'none'
        },
        legend: {
            position: 'none'
        }
    };
    inventeringsAr: number;
    data: any[][];
    legendData: any[];

    showAsteriskTextTall: boolean = false;
    showAsteriskTextGran: boolean = false;

    private _omradeNamn = new BehaviorSubject<string>(null);
    omradeNamn$ = this._omradeNamn.asObservable();

    constructor(public colormapService: ColorMapService) {}

    ngOnInit(): void {}

    onOmradeSelect(omrade: ButtonGroupItemData) {
        if (omrade && omrade.inventeringar && omrade.inventeringar.length > 0) {
            this._omradeNamn.next(omrade.omradeName);
            this.generateTableData(omrade.inventeringar[0]);
            this.generateChartData(omrade.inventeringar[0]);
        }
    }

    generateTableData(senasteInv: AbinResultDto) {
        this.tableConfig = {
            columns: [
                { title: 'Trädslag', dataProperty: 'tradslag' },
                { title: 'Alla bestånd', dataProperty: 'allaBestand' },
                { title: 'Föryngrad med tall', dataProperty: 'foryngradMedTall' },
                { title: 'Föryngrad med gran', dataProperty: 'foryngradMedGran' }
            ],
            rowActions: []
        };
        const totalFordelning = this.getForyngradeTradslagTotal(senasteInv);
        const getTallAsterisk = this.getAsterisk(senasteInv.foryngTallHa3InvMedel, totalFordelning);
        const getGranAsterisk = this.getAsterisk(senasteInv.foryngGranHa3InvMedel, totalFordelning, true);
        this.tableContent = [
            {
                tradslag: 'Tall',
                allaBestand: formatNumber(senasteInv.antalTallarHa3InvAVG, 'sv'),
                foryngradMedTall: formatNumber(senasteInv.antalTallarForyngTallHa3InvAVG, 'sv') + getTallAsterisk,
                foryngradMedGran: formatNumber(senasteInv.antalTallarForyngGranHa3InvAVG, 'sv') + getGranAsterisk
            },
            {
                tradslag: 'Gran',
                allaBestand: formatNumber(senasteInv.antalGranarHa3InvAVG, 'sv'),
                foryngradMedTall: formatNumber(senasteInv.antalGranarForyngTallHa3InvAVG, 'sv') + getTallAsterisk,
                foryngradMedGran: formatNumber(senasteInv.antalGranarForyngGranHa3InvAVG, 'sv') + getGranAsterisk
            },

            {
                tradslag: 'Björk',
                allaBestand: formatNumber(senasteInv.antalBjorkarHa3InvAVG, 'sv'),
                foryngradMedTall: formatNumber(senasteInv.antalBjorkarForyngTallHa3InvAVG, 'sv') + getTallAsterisk,
                foryngradMedGran: formatNumber(senasteInv.antalBjorkarForyngGranHa3InvAVG, 'sv') + getGranAsterisk
            },
            {
                tradslag: 'Övriga produktionsträdslag',
                allaBestand: formatNumber(senasteInv.antalOvrigtHa3InvAVG, 'sv'),
                foryngradMedTall: formatNumber(senasteInv.antalOvrigtForyngTallHa3InvAVG, 'sv') + getTallAsterisk,
                foryngradMedGran: formatNumber(senasteInv.antalOvrigtForyngGranHa3InvAVG, 'sv') + getGranAsterisk
            },
            {
                tradslag: 'RASE',
                allaBestand: formatNumber(senasteInv.antalRASEHa3InvAVG, 'sv'),
                foryngradMedTall: formatNumber(senasteInv.antalRASEForyngTallHa3InvAVG, 'sv') + getTallAsterisk,
                foryngradMedGran: formatNumber(senasteInv.antalRASEForyngGranHa3InvAVG, 'sv') + getGranAsterisk
            },
            {
                tradslag: 'Totalt',
                allaBestand: formatNumber(this.getTotalStammarHa3Inv(senasteInv), 'sv'),
                foryngradMedTall: formatNumber(this.getTotalForyngradMedTall(senasteInv), 'sv') + getTallAsterisk,
                foryngradMedGran: formatNumber(this.getTotalForyngradMedGran(senasteInv), 'sv') + getGranAsterisk
            }
        ];

        this.showAsteriskTextTall = getTallAsterisk !== '';
        this.showAsteriskTextGran = getGranAsterisk !== '';
    }
    private getAsterisk(medelVarde: number, totalFordelning: number, isGran: boolean = false): string {
        const value = Math.round((medelVarde / totalFordelning) * 100);
        // Är andelen mindre än 20% så ska vi visa en asterisk.
        if (value < 20 && !isGran) {
            return ' *';
        } else if (value < 20 && isGran) {
            return ' **';
        }
        return '';
    }

    getTotalForyngradMedTall(inv: AbinResultDto): number {
        return Math.round(
            (inv.antalTallarForyngTallHa3InvAVG || 0) +
                (inv.antalGranarForyngTallHa3InvAVG || 0) +
                (inv.antalBjorkarForyngTallHa3InvAVG || 0) +
                (inv.antalOvrigtForyngTallHa3InvAVG || 0) +
                (inv.antalRASEForyngTallHa3InvAVG || 0)
        );
    }

    getTotalForyngradMedGran(inv: AbinResultDto): number {
        return Math.round(
            (inv.antalTallarForyngGranHa3InvAVG || 0) +
                (inv.antalGranarForyngGranHa3InvAVG || 0) +
                (inv.antalBjorkarForyngGranHa3InvAVG || 0) +
                (inv.antalOvrigtForyngGranHa3InvAVG || 0) +
                (inv.antalRASEForyngGranHa3InvAVG || 0)
        );
    }

    getTotalStammarHa3Inv(inv: AbinResultDto): number {
        return Math.round(
            (inv.antalTallarHa3InvAVG || 0) +
                (inv.antalGranarHa3InvAVG || 0) +
                (inv.antalBjorkarHa3InvAVG || 0) +
                (inv.antalOvrigtHa3InvAVG || 0) +
                (inv.antalRASEHa3InvAVG || 0)
        );
    }

    generateChartData(senasteInventering: AbinResultDto) {
        const data: any[][] = [['Träslag', 'Procent']];
        const colors = [];
        const legendData = [];
        this.inventeringsAr = senasteInventering.invAr;
        const total = this.getTotalStammarHa3InvMedel(senasteInventering);
        for (const entry of this.chartEntries) {
            if (senasteInventering[entry.key]) {
                const name = entry.name;
                const value = round((senasteInventering[entry.key] / total) * 100, 0);
                const color = this.colormapService.getColor(entry.color);
                data.push([name, value]);
                colors.push(color);
                legendData.push({ name, value, color });
            }
        }
        this.data = data;
        this.options = { ...this.options, colors };
        this.legendData = legendData;
    }

    getTotalStammarHa3InvMedel(inv: AbinResultDto): number {
        let sumTotal = 0;
        for (const entry of this.chartEntries) {
            const value = inv[entry.key];
            sumTotal += value || 0;
        }
        return Math.round(sumTotal);
    }

    /**
     * Return the total number of foryngrade tradslag in the given inventory
     * @param {AbinResultDto} inv the inventory to get the total from
     * @return {number} the total number of foryngrade tradslag
     */
    getForyngradeTradslagTotal(inv: AbinResultDto): number {
        return (
            (inv.foryngTallHa3InvMedel || 0) +
            (inv.foryngBjorkHa3InvMedel || 0) +
            (inv.foryngContortaHa3InvMedel || 0) +
            (inv.foryngGranHa3InvMedel || 0) +
            (inv.foryngIngenHa3InvMedel || 0) +
            (inv.foryngLarkHa3InvMedel || 0) +
            (inv.foryngOvrigtHa3InvMedel || 0)
        );
    }
}
